/**
 * Aplica a identidade visual da barbearia.
 *
 * O servidor já entrega o tema com o contraste corrigido (ver `buildTenantTheme`
 * no core), então aqui é só despejar as variáveis CSS. Trocar a cor no painel
 * repinta o app inteiro na hora, sem rebuild e sem recarregar.
 */

import type { TenantTheme } from '@barbearia/core';
import type { ThemeMode } from '@barbearia/shared';

const STORAGE_KEY = 'barbearia:tema';

export type ResolvedMode = 'light' | 'dark';

/** O que o usuário escolheu na engrenagem, se escolheu. */
export function storedPreference(): ThemeMode | null {
  const value = localStorage.getItem(STORAGE_KEY);
  return value === 'LIGHT' || value === 'DARK' || value === 'SYSTEM' ? value : null;
}

export function storePreference(mode: ThemeMode): void {
  localStorage.setItem(STORAGE_KEY, mode);
}

export function systemMode(): ResolvedMode {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * A escolha do usuário vence a da barbearia, que vence a do sistema.
 * Quem está com o celular na mão decide o que enxerga.
 */
export function resolveMode(tenantMode: ThemeMode): ResolvedMode {
  const preference = storedPreference() ?? tenantMode;
  if (preference === 'LIGHT') return 'light';
  if (preference === 'DARK') return 'dark';
  return systemMode();
}

export function applyTheme(theme: TenantTheme, mode: ResolvedMode): void {
  const root = document.documentElement;
  const colors = theme[mode];

  root.dataset.theme = mode;
  root.style.setProperty('--color-primary', colors.primary);
  root.style.setProperty('--color-primary-hover', colors.primaryHover);
  root.style.setProperty('--color-primary-ink', colors.primaryForeground);
  root.style.setProperty('--color-accent', colors.accent);
  root.style.setProperty('--color-accent-ink', colors.accentForeground);

  // A barra de status do iPhone acompanha a cor de fundo quando instalado.
  const meta = document.querySelector('meta[name="theme-color"]');
  meta?.setAttribute('content', mode === 'dark' ? '#16161A' : '#FFFFFF');
}

/**
 * O ícone que o iPhone grava na tela de início.
 *
 * O manifesto por barbearia (`GET /tenant/manifest.webmanifest`) resolve o
 * Android; o iOS dá precedência ao `apple-touch-icon` do documento, e o
 * `index.html` é o mesmo arquivo para todas as barbearias. Então o ícone da
 * barbearia só pode entrar aqui, quando já se sabe qual barbearia é — mesma
 * razão pela qual o tema é aplicado em tempo de execução.
 *
 * O que sobe é o WebP do próprio upload. Se algum iOS recusar o formato, o
 * caminho já decidido é gerar um PNG de 180px no navegador junto com o upload
 * do ícone, em `Branding` (ver docs/debitos-tecnicos.md).
 */
export function applyHomeScreenIcon(iconUrl: string | null): void {
  const link = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]');
  if (!link) return;

  // Sem logo, fica o PNG estático do index.html — a tesoura na cor da marca.
  link.href = iconUrl ?? '/icone-180.png';
}

/** Avisa quando o sistema troca de claro para escuro (ou o contrário). */
export function watchSystemMode(onChange: (mode: ResolvedMode) => void): () => void {
  const query = window.matchMedia('(prefers-color-scheme: dark)');
  const listener = (event: MediaQueryListEvent) => onChange(event.matches ? 'dark' : 'light');
  query.addEventListener('change', listener);
  return () => query.removeEventListener('change', listener);
}
